import React, { useCallback, useEffect, useState } from "react";
import { Button, Modal, TextInput } from "flowbite-react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "../../Reducers/store";
import { UserLevelNames } from "../../Models/UserLevel";
import EmailsApi, {
  EmailSentDetail,
  EmailSentListItem,
} from "../../Api/EmailsApi";
import Loader from "../Common/Loader";

function todayLocalIsoDate(): string {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function formatSentAt(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  // Always show Eastern — DateSent is a UTC instant (API may include Eastern offset).
  return d.toLocaleString("en-US", {
    timeZone: "America/New_York",
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

const AdminEmailsSentPage: React.FC = () => {
  const navigate = useNavigate();
  const { userModel } = useAppSelector((state) => state.user);
  const today = todayLocalIsoDate();
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [search, setSearch] = useState("");
  const [searchApplied, setSearchApplied] = useState("");
  const [startApplied, setStartApplied] = useState(today);
  const [endApplied, setEndApplied] = useState(today);
  const [rows, setRows] = useState<EmailSentListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState<EmailSentDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    if (userModel && userModel.userLevelId !== UserLevelNames.SuperAdmin) {
      toast.error("You do not have permission to access this page.");
      navigate("/");
    }
  }, [userModel, navigate]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const from = startApplied <= endApplied ? startApplied : endApplied;
      const to = startApplied <= endApplied ? endApplied : startApplied;
      const res = await EmailsApi.getSent(from, to, searchApplied);
      setRows(res.data);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load emails sent.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [startApplied, endApplied, searchApplied]);

  useEffect(() => {
    if (userModel?.userLevelId === UserLevelNames.SuperAdmin) {
      load();
    }
  }, [userModel, load]);

  const openDetail = async (id: number) => {
    try {
      setDetailLoading(true);
      const res = await EmailsApi.getSentDetail(id);
      setDetail(res.data);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load email detail.");
    } finally {
      setDetailLoading(false);
    }
  };

  const applyFilters = (e: React.FormEvent) => {
    e.preventDefault();
    if (!startDate || !endDate) {
      toast.error("Select a start and end date.");
      return;
    }
    setStartApplied(startDate);
    setEndApplied(endDate);
    setSearchApplied(search);
  };

  return (
    <div className="w-full">
      <div className="w-full h-[74px] p-4">
        <div className="mx-1 flex items-end">
          <div className="flex flex-1">
            <h1 className="text-gray-900 font-bold text-4xl">Emails Sent</h1>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-4">
        <form
          onSubmit={applyFilters}
          className="flex flex-wrap items-end gap-3"
        >
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              From
            </label>
            <TextInput
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              sizing="md"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              To
            </label>
            <TextInput
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              sizing="md"
            />
          </div>
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Search
            </label>
            <TextInput
              type="text"
              placeholder="Recipient, subject, member…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sizing="md"
            />
          </div>
          <Button type="submit" className="buttonMain">
            Apply
          </Button>
        </form>

        {loading ? (
          <Loader />
        ) : (
          <div className="overflow-x-auto bg-white border border-gray-200 rounded-lg">
            <table className="min-w-full text-sm text-left">
              <thead className="bg-gray-50 text-gray-700">
                <tr>
                  <th className="px-3 py-2 font-semibold">Sent</th>
                  <th className="px-3 py-2 font-semibold">To</th>
                  <th className="px-3 py-2 font-semibold">Subject</th>
                  <th className="px-3 py-2 font-semibold">Newsletter</th>
                  <th className="px-3 py-2 font-semibold">Member / User</th>
                  <th className="px-3 py-2 font-semibold"></th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-3 py-6 text-center text-gray-500"
                    >
                      No emails found for this date range.
                    </td>
                  </tr>
                ) : (
                  rows.map((row) => (
                    <tr
                      key={row.id}
                      className="border-t border-gray-100 hover:bg-gray-50"
                    >
                      <td className="px-3 py-2 whitespace-nowrap">
                        {formatSentAt(row.dateSent)}
                      </td>
                      <td className="px-3 py-2">{row.emailAddress || "—"}</td>
                      <td className="px-3 py-2">{row.subject || "—"}</td>
                      <td className="px-3 py-2">
                        {row.newsletterName || "—"}
                      </td>
                      <td className="px-3 py-2">
                        {row.memberName || row.userName || "—"}
                        {row.memberName && row.userName
                          ? ` / ${row.userName}`
                          : ""}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {row.hasHtml ? (
                          <button
                            type="button"
                            className="text-blue-700 font-medium hover:underline"
                            onClick={() => openDetail(row.id)}
                          >
                            View
                          </button>
                        ) : (
                          <span className="text-gray-400">No body</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal
        show={detail != null || detailLoading}
        size="4xl"
        onClose={() => setDetail(null)}
        dismissible
      >
        <Modal.Header>
          {detail?.subject || "Email"} — {detail?.emailAddress || ""}
        </Modal.Header>
        <Modal.Body>
          {detailLoading && !detail ? (
            <Loader />
          ) : detail ? (
            <div className="space-y-3">
              <div className="text-sm text-gray-600">
                {detail.dateSent ? formatSentAt(detail.dateSent) : ""}
                {detail.newsletterName
                  ? ` · Newsletter: ${detail.newsletterName}`
                  : ""}
              </div>
              <iframe
                title="Email HTML preview"
                sandbox=""
                className="w-full min-h-[60vh] border border-gray-200 rounded bg-white"
                srcDoc={detail.html || "<p>No HTML body stored.</p>"}
              />
            </div>
          ) : null}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default AdminEmailsSentPage;
